import { Component, effect, inject, input, output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { EntityPropertyDataTypeEnum } from '@app/core/models/entity-property-data-type.enum';
import { IEntityPropertyValueValid } from '@app/core/models/entity-property.model';
import { IEntityProperty } from '@app/core/models/entity-property.model';
import { _ } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';

import { KENDO_FORM, KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';

_(['App.UnitCode',
  'EntityTranslation.ValidValues'
]);

@Component({
  selector: 'app-select-valid-value',
  templateUrl: './select-valid-value.component.html',
  styleUrls: ['./select-valid-value.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SelectValidValueComponent,
    },
  ],
  imports: [KENDO_FORM, KENDO_DROPDOWNS, KENDO_INPUTS, FormsModule],
})
export class SelectValidValueComponent implements ControlValueAccessor {
  validValues: IEntityPropertyValueValid[] = [];
  value: any = null; // the reason to declare this var as any is that it can hold a string or an int or a double value
  public valueField = '';
  public textField = '';

  private readonly translateService = inject(TranslateService);
  label = input<string>(this.translateService.instant('EntityTranslation.ValidValues'));
  propertyCode = input<string>('');
  entityGroupIndex = input<number>();
  property = input.required<IEntityProperty>();

  dataTypeChange = output<number>();
  isMandatory = output<boolean>();

  private onTouched!: Function;
  private onChanged!: Function;

  constructor() {
    effect(() => {
      let prop = this.property();

      if (prop.code == null) {
        return;
      }
      this.validValues = prop.validValues;

      if (prop.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_STRING) {
        this.valueField = 'valueString';
        this.value = this.value == null ? null : String(this.value);
      } else if (prop.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_INT || prop.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_DOUBLE) {
        this.valueField = prop.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_INT ? 'valueInt' : 'valueDouble';
        const numericValue = Number(this.value);
        this.value = this.value == null || Number.isNaN(numericValue) ? null : numericValue;
      } else {
        throw new Error('Invalid datatype for property: ' + this.propertyCode);
      }

      if (prop.areValuesValidated) {
        this.textField = prop.validValues?.some(x => x.description) ? 'description' : this.valueField;
      } else {
        this.textField = this.valueField;
      }

      this.dataTypeChange.emit(prop.dataType);
      this.isMandatory.emit(prop.entityGroupProperties?.[0]?.isMandatory ?? false);
    });
  }

  onChangedVal(validValue: IEntityPropertyValueValid) {
    if (!validValue) {
      this.value = null;
    } else if (this.property()?.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_STRING) {
      this.value = validValue.valueString;
    } else if (this.property()?.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_INT) {
      this.value = Number(validValue.valueInt);
    } else if (this.property()?.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_DOUBLE) {
      this.value = Number(validValue.valueDouble);
    }

    this.onTouched();
    this.onChanged(this.value);
  }

  onChange(value: any) {
    this.value = value;

    this.onTouched();
    this.onChanged(this.value);
  }

  writeValue(validValue: any): void {
    if (!validValue) {
      return;
    }

    if (this.property()?.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_DOUBLE || this.property()?.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_INT) {
      this.value = Number(validValue);
    } else {
      this.value = String(validValue);
    }
  }

  registerOnChange(onChange: any): void {
    this.onChanged = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  isString(): boolean {
    return this.property()?.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_STRING;
  }

  isInt(): boolean {
    return this.property()?.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_INT;
  }

  isDouble(): boolean {
    return this.property()?.dataType === EntityPropertyDataTypeEnum.DATA_TYPE_DOUBLE;
  }

  isValidValues(): boolean {
    return this.property()?.areValuesValidated ?? false;
  }
}
